import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, Typography } from '@mui/material';
import { loadHereMapsApi } from '../../utils/hereMapsLoader';

interface HereMapsProps {
  apiKey: string;
  latitude: number;
  longitude: number;
  height?: number;
  disableDragging?: boolean;
  showInfoCard?: boolean;
  address?: string;
  addressLine2?: string;
  onLocationChange: (address: string, lat: number, lng: number) => void;
  zoom?: number;
}

interface MarkerPosition {
  x: number;
  y: number;
}

const HereMaps: React.FC<HereMapsProps> = ({
  latitude,
  longitude,
  height,
  showInfoCard,
  address,
  apiKey,
  addressLine2,
  disableDragging,
  onLocationChange,
  zoom = 15,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any | null>(null);
  const [marker, setMarker] = useState<any | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isInternalUpdate, setIsInternalUpdate] = useState(false);
  const [markerScreenPosition, setMarkerScreenPosition] =
    useState<MarkerPosition>({ x: 0, y: 0 });
  const isInitialized = useRef(false);
  const resizeObserver = useRef<ResizeObserver | null>(null);

  const updateMarkerPosition = () => {
    if (map && marker && mapRef.current) {
      const markerGeometry = marker.getGeometry();
      const position = map.geoToScreen(markerGeometry);

      // Request animation frame to ensure we have latest position
      requestAnimationFrame(() => {
        setMarkerScreenPosition({
          x: position.x,
          y: position.y,
        });
      });
    }
  };

  const updateMapZoom = (zoom: number) => {
    if (map && mapRef.current) {
      setTimeout(() => {
        map.setZoom(zoom, false);
      }, 500);
    }
  };

  useEffect(() => {
    updateMarkerPosition();
  }, [map, marker, latitude, longitude]);

  useEffect(() => {
    if (zoom) updateMapZoom(zoom);
  }, [map, zoom]);

  // Add listener for marker position updates
  useEffect(() => {
    let animationFrameId: number;

    const checkPosition = () => {
      updateMarkerPosition();
      animationFrameId = requestAnimationFrame(checkPosition);
    };

    if (map && marker && !isDragging) {
      checkPosition();
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [map, marker, isDragging]);

  useEffect(() => {
    if (!isInitialized.current) {
      loadHereMapsApi(() => {
        initMap();
      });
      isInitialized.current = true;
    }
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []);

  const initMap = () => {
    const { H } = window as any;

    if (!mapRef.current) return;

    const platform = new H.service.Platform({
      apikey: apiKey,
    });

    // Initialize the engine type:
    const engineType = H.Map.EngineType['HARP'];

    // Obtain the default map types from the platform object:
    const defaultLayers = platform.createDefaultLayers({ engineType });

    // Instantiate (and display) a map:
    const newMap = new H.Map(mapRef.current, defaultLayers.vector.normal.map, {
      engineType,
      center: { lat: latitude, lng: longitude },
      zoom,
    });

    // MapEvents enables the event system.
    // The behavior variable implements default interactions for pan/zoom (also on mobile touch environments).
    const behavior = new H.mapevents.Behavior(
      new H.mapevents.MapEvents(newMap),
    );
    const ui = H.ui.UI.createDefault(newMap, defaultLayers);

    setMap(newMap);

    // Add a marker
    const newMarker = new H.map.Marker(
      { lat: latitude, lng: longitude },
      {
        // mark the object as volatile for smooth dragging
        volatility: true,
      },
    );
    newMarker.draggable = true;

    newMap.addObject(newMarker);
    setMarker(newMarker);

    const updateDragPosition = (pointer: any) => {
      if (disableDragging || !mapRef.current) return;
      const rect = mapRef.current.getBoundingClientRect();
      setMarkerScreenPosition({
        x: pointer.viewportX - rect.left,
        y: pointer.viewportY - rect.top,
      });
    };

    // Initialize the map with current size
    setTimeout(() => {
      if (newMap) {
        newMap.getViewPort().resize();
      }
    }, 0);

    // Enable dynamic resizing of the map, based on the current size of the enclosing cntainer when resizing

    // Set up a resize observer to handle container size changes
    resizeObserver.current = new ResizeObserver(() => {
      if (newMap) {
        // Force the map to resize when container changes size
        setTimeout(() => {
          newMap.getViewPort().resize();
          updateMarkerPosition();
        }, 0);
      } else {
        // Queue a resize check for when the map becomes available
        setTimeout(() => {
          updateMarkerPosition();
        }, 100);
      }
    });

    // Start observing the map container
    if (mapRef.current) {
      resizeObserver.current.observe(mapRef.current);
    }

    newMap.addEventListener(
      'dragstart',
      function (ev: any) {
        if (disableDragging) {
          return;
        }
        var target = ev.target,
          pointer = ev.currentPointer;
        if (target instanceof H.map.Marker) {
          setIsInternalUpdate(true); // Set flag when starting drag

          // Change cursor to grabbing when dragging starts
          const mapContainer = mapRef.current;
          if (mapContainer) {
            mapContainer.style.cursor = 'grabbing';
          }

          var targetPosition = newMap.geoToScreen(target.getGeometry());
          target['offset'] = new H.math.Point(
            pointer.viewportX - targetPosition.x,
            pointer.viewportY - targetPosition.y,
          );
          behavior.disable();
          setIsDragging(true);
          updateDragPosition(pointer);
        }
      },
      false,
    );

    newMap.addEventListener(
      'drag',
      function (ev: any) {
        if (disableDragging) {
          return;
        }
        var target = ev.target,
          pointer = ev.currentPointer;
        if (target instanceof H.map.Marker) {
          target.setGeometry(
            newMap.screenToGeo(
              pointer.viewportX - target['offset'].x,
              pointer.viewportY - target['offset'].y,
            ),
          );
          updateDragPosition(pointer);
        }
      },
      false,
    );

    newMap.addEventListener(
      'dragend',
      function (ev: any) {
        if (disableDragging) {
          return;
        }
        var target = ev.target;
        if (target instanceof H.map.Marker) {
          behavior.enable();
          setIsDragging(false);

          const coord = newMap.screenToGeo(
            ev.currentPointer.viewportX,
            ev.currentPointer.viewportY,
          );

          onLocationChange('', coord.lat, coord.lng);
        }
      },
      false,
    );

    newMap.addEventListener('mapviewchange', () => {
      updateMarkerPosition();
    });

    // Add hover effect for the marker
    newMap.addEventListener('pointermove', function (ev: any) {
      const target = ev.target;
      const mapContainer = mapRef.current;

      if (mapContainer) {
        if (target instanceof H.map.Marker && !disableDragging) {
          // Show grab cursor when hovering over marker
          mapContainer.style.cursor = 'grab';
        } else {
          // Reset to default cursor
          mapContainer.style.cursor = 'default';
        }
      }
    });

    // Reset cursor when mouse leaves the map
    newMap.addEventListener('pointerleave', function () {
      const mapContainer = mapRef.current;
      if (mapContainer) {
        mapContainer.style.cursor = 'default';
      }
    });
  };

  useEffect(() => {
    if (map && marker) {
      if (
        !isValidCoord(latitude) ||
        !isValidCoord(longitude) ||
        isInternalUpdate
      ) {
        setIsInternalUpdate(false);
        return;
      }

      const validLat = !isNaN(latitude) ? latitude : 0;
      const validLng = !isNaN(longitude) ? longitude : 0;
      const newPosition = { lat: validLat, lng: validLng };
      marker.setGeometry(newPosition);
      map.setCenter(newPosition);
    }
  }, [map, marker, latitude, longitude]);

  return (
    <Box
      ref={mapRef}
      sx={{
        width: '100%',
        height: height || 400,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {showInfoCard && !isDragging && (
        <Box
          sx={{
            position: 'absolute',
            left: `${markerScreenPosition.x}px`,
            top: `${markerScreenPosition.y + 20}px`, // Moved below marker
            transform: 'translate(-50%, 0)', // Changed transform to not move up
            pointerEvents: 'none',
            zIndex: 800,
          }}
        >
          <Card
            sx={{
              width: 280,
              p: 2,
              position: 'relative',
              '&:before': {
                // Changed to before for top arrow
                content: '""',
                position: 'absolute',
                top: -8,
                left: '50%',
                transform: 'translate(-50%, 0) rotate(45deg)',
                width: 16,
                height: 16,
                bgcolor: 'background.paper',
                zIndex: -1,
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1,
                letterSpacing: '0.1em',
              }}
            >
              DRAG PIN TO ADJUST LOCATION
            </Typography>
            {address && (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {address}
              </Typography>
            )}
            {addressLine2 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {addressLine2}
              </Typography>
            )}
          </Card>
        </Box>
      )}
    </Box>
  );
};

function isValidCoord(number: any) {
  return (
    !isNaN(number) &&
    number !== '' &&
    number !== null &&
    typeof number !== 'undefined'
  );
}

export default HereMaps;
